import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthStore } from '@stores/auth-store';

import Header from '../../components/header/header.component';
import Table from '../../components/table/table.component';
import { invoiceSdk } from '../../services/invoice.service';
import ResponseCodes from '../../constants/response.codes';
import { Invoice } from '../../types/invoice.type';
import NumberUtil from '../../utils/number.util';
import { mapInvoiceStatus } from '../../utils/invoice-status.util';
import ModuleSummary from '../../components/module-summary/module-summary.component';
import { TableRow } from '../../types/table-row.type';
import BasicButton from '../../components/basic-button/basic-button.component';
import { useThemeStore } from '../../store';
import FilteringButton from '../../components/filtering-button/filtering-button.component';
import View from '../../components/view/view';

import styles from './invoices.module.scss';

const InvoicesView = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams({ page: '1', count: '10' });

    const { theme } = useThemeStore();
    const { permissions } = useAuthStore();

    const [totalPages, setTotalPages] = useState(1);
    const [invoiceTableRows, setInvoiceTableRows] = useState<TableRow[]>([]);

    useEffect(() => {
        fetchInvoices(mapFilters(params));
    }, [params]);

    const mapFilters = useCallback((params: URLSearchParams) => {
        const filters: Record<string, any> = {};
        filters.page = Number(params.get('page'));
        filters.count = Number(params.get('count'));
        if (params.get('id')) {
            filters.id = Number(params.get('id'));
        }
        if (params.get('payerId')) {
            filters.payerId = Number(params.get('payerId'));
        }

        return filters;
    }, []);

    const fetchInvoices = useCallback(
        async (queryParams?: any) => {
            if (!queryParams.page) return;
            const response = await invoiceSdk.getAll(queryParams);
            if (response.code !== ResponseCodes.PROCESS_OK) {
                console.error('Error getting invoices:', response);
                return;
            }
            const { total } = response.data;
            setTotalPages(Math.ceil(total / Number(params.get('count'))));
            mapRows(response.data.result);
        },
        [params]
    );

    const mapRows = (invoices: Invoice[]) => {
        const invoiceRows = invoices.map((invoice) => {
            return {
                id: invoice.id,
                data: [
                    { type: 'number', value: invoice.id },
                    {
                        type: 'string',
                        value: `${invoice.payer.firstName} ${
                            invoice.payer.lastName ?? ''
                        }`,
                    },
                    {
                        type: 'string',
                        value: NumberUtil.formatCurrency(invoice.total, 'USD'),
                    },
                    { type: 'status', value: mapInvoiceStatus(invoice.status) },
                    {
                        type: 'date',
                        value: new Date(invoice.createdAt),
                    },
                ],
            };
        });

        setInvoiceTableRows(invoiceRows);
    };

    const goToPage = useCallback(
        (page: number) => {
            if (page < 1 || page > totalPages) return;
            params.set('page', page.toString());
            setParams(params);
        },
        [params, totalPages]
    );

    const goToInvoiceDetails = useCallback(
        (id: number) => navigate(`/invoices/${id}`),
        []
    );

    const goToCreateInvoice = useCallback(() => {
        navigate('/invoices/create');
    }, []);

    const addFilter = useCallback(
        (attr: string, value: string) => {
            if (!value || value.trim().length === 0) {
                params.delete(attr);
            } else {
                params.set(attr, value);
            }
            params.set('page', '1');
            setParams(params);
        },
        [params]
    );

    return (
        <View permissions={permissions} requiredPermission="read-invoices">
            <Header
                mode={theme}
                title={'Facturas'}
                subtitle={'Aquí puedes ver el estado de todas las facturas'}
            />
            <ModuleSummary
                mode={theme}
                summary={[
                    { label: 'Total pagos pendientes', value: '-' },
                    { label: 'Facturas pendientes del mes', value: '-' },
                    { label: 'Total facturas del mes', value: '-' },
                    { label: 'Fecha última factura', value: '-' },
                ]}
            />
            <article className={styles.mainButtonsContainer}>
                <BasicButton onClick={goToCreateInvoice}>
                    Crear nueva factura
                </BasicButton>
            </article>
            <div className={'view-filtering-section'}>
                <span className={'filtering-label'}>Buscar por:</span>
                <FilteringButton
                    defaultValue={params.get('id')}
                    text={'ID de factura'}
                    onSetFilter={(id) => addFilter('id', id)}
                />
                <FilteringButton
                    defaultValue={params.get('payerId')}
                    text={'ID de usuario'}
                    onSetFilter={(id) => addFilter('payerId', id)}
                />
            </div>
            <Table
                mode={theme}
                header={[
                    'N° Factura',
                    'Pagador',
                    'Total',
                    'Estado',
                    'Fecha creación',
                ]}
                data={invoiceTableRows}
                currentPage={Number(params.get('page'))}
                totalPages={totalPages}
                onItemSelected={goToInvoiceDetails}
                onPageChange={goToPage}
            />
        </View>
    );
};

export default InvoicesView;
